import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Switch,
  Title,
  ToolbarItem,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { MoneyBillWaveIcon, SearchIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useMollieSettlement } from '../../../hooks/useAdminReconciliation';
import type {
  MollieSettlementDetail,
  MollieSettlementLine,
  MollieSettlementPeriodDetail,
} from '../../../types/models/Reconciliation';
import { brusselsToday, formatInstantBrussels, formatMonthYear } from '../../../utils/dateFormat';
import { StatusMultiSelect } from '../Verda/VerdaGpuParts';
import {
  AMOUNT_CELL,
  CheckLabel,
  CsvButton,
  EmptyListState,
  ExactMoney,
  FetchedAt,
  FlagLabels,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  StaleDataAlert,
  distinctOptions,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import { DetailList, attributionItems } from './AttributionDetails';
import { SettlementPeriodsTable, SettlementStatusLabel } from './SettlementsTab';
import {
  COST_CLASS_LABELS,
  PAYMENT_METHOD_LABELS,
  PURPOSE_LABELS,
  SETTLEMENT_LINE_KIND_LABELS,
  labelOf,
} from './reconciliationLabels';
import { buildSettlementLinesCsv, reconciliationCsvFilename } from './reconciliationCsv';

const matchLine = (l: MollieSettlementLine, q: string) =>
  matchesQuery(q, [l.client_name, l.invoice_numero, l.description, l.id, l.payment_id, l.domains]);

/** Mollie's own revenue and cost lines for one period (English data, Mollie's precision). */
const PeriodLinesCard: React.FunctionComponent<{ period: MollieSettlementPeriodDetail }> = ({ period }) => (
  <Card>
    <CardTitle>
      {formatMonthYear(period.period)}
      {period.mollie_invoice_reference && (
        <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
          {' '}
          — facture Mollie {period.mollie_invoice_reference}
        </span>
      )}
    </CardTitle>
    <CardBody>
      <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
        Revenus
      </Title>
      {period.revenue_lines.length === 0 ? (
        <EmptyListState title="Aucune ligne de revenus" />
      ) : (
        <Table
          aria-label={`Lignes de revenus de ${formatMonthYear(period.period)}`}
          variant="compact"
          className="pf-v6-u-mb-lg"
        >
          <Thead>
            <Tr>
              <Th>Description (Mollie)</Th>
              <Th className={AMOUNT_CELL}>Nombre</Th>
              <Th className={AMOUNT_CELL}>Net</Th>
              <Th className={AMOUNT_CELL}>TVA</Th>
              <Th className={AMOUNT_CELL}>Brut</Th>
            </Tr>
          </Thead>
          <Tbody>
            {period.revenue_lines.map((l, i) => (
              <Tr key={`${l.description}-${i}`}>
                <Td dataLabel="Description (Mollie)">{l.description ?? '—'}</Td>
                <Td dataLabel="Nombre" className={AMOUNT_CELL}>
                  {l.count ?? '—'}
                </Td>
                <Td dataLabel="Net" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_net} />
                </Td>
                <Td dataLabel="TVA" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_vat} />
                </Td>
                <Td dataLabel="Brut" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_gross} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      <Title headingLevel="h4" size="md" className="pf-v6-u-mb-sm">
        Frais
      </Title>
      {period.cost_lines.length === 0 ? (
        <EmptyListState title="Aucune ligne de frais" />
      ) : (
        <Table aria-label={`Lignes de frais de ${formatMonthYear(period.period)}`} variant="compact">
          <Thead>
            <Tr>
              <Th>Catégorie</Th>
              <Th>Description (Mollie)</Th>
              <Th className={AMOUNT_CELL}>Nombre</Th>
              <Th className={AMOUNT_CELL}>Tarif</Th>
              <Th className={AMOUNT_CELL}>Net</Th>
              <Th className={AMOUNT_CELL}>TVA</Th>
              <Th className={AMOUNT_CELL}>Brut</Th>
            </Tr>
          </Thead>
          <Tbody>
            {period.cost_lines.map((l, i) => (
              <Tr key={`${l.class}-${l.description}-${i}`}>
                <Td dataLabel="Catégorie">{labelOf(COST_CLASS_LABELS, l.class)}</Td>
                <Td dataLabel="Description (Mollie)">{l.description ?? '—'}</Td>
                <Td dataLabel="Nombre" className={AMOUNT_CELL}>
                  {l.count ?? '—'}
                </Td>
                <Td dataLabel="Tarif" className={AMOUNT_CELL}>
                  {l.rate_fixed === null && l.rate_percentage === null ? (
                    '—'
                  ) : (
                    <>
                      <ExactMoney value={l.rate_fixed} /> + {l.rate_percentage ?? '0'} %
                    </>
                  )}
                </Td>
                <Td dataLabel="Net" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_net} />
                </Td>
                <Td dataLabel="TVA" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_vat} />
                </Td>
                <Td dataLabel="Brut" className={AMOUNT_CELL}>
                  <ExactMoney value={l.amount_gross} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </CardBody>
  </Card>
);

const LINE_COLUMN_COUNT = 9;

/** Every payment / refund / chargeback / capture the payout carried. */
const SettlementLinesSection: React.FunctionComponent<{ detail: MollieSettlementDetail }> = ({ detail }) => {
  const rows = detail.lines;
  const [kinds, setKinds] = useState<string[]>([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const kindOptions = useMemo(
    () => distinctOptions([...rows.map((l) => l.kind), ...kinds], (v) => labelOf(SETTLEMENT_LINE_KIND_LABELS, v)),
    [rows, kinds],
  );
  const predicate = useCallback(
    (l: MollieSettlementLine) => (kinds.length === 0 || kinds.includes(l.kind)) && (!flaggedOnly || l.flags.length > 0),
    [kinds, flaggedOnly],
  );
  const filterKey = kinds.length > 0 || flaggedOnly ? JSON.stringify([kinds, flaggedOnly]) : '';
  const list = useClientList(rows, matchLine, predicate, filterKey);
  const clearFilters = () => {
    list.setSearchValue('');
    setKinds([]);
    setFlaggedOnly(false);
  };
  const rowKey = (l: MollieSettlementLine, i: number) => `${l.kind}-${l.id ?? i}`;
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const { settlement } = detail;

  return (
    <Card>
      <CardTitle>Opérations portées par le versement</CardTitle>
      <CardBody>
        <DescriptionList
          isCompact
          isHorizontal
          columnModifier={{ default: '1Col', md: '2Col' }}
          className="pf-v6-u-mb-md"
        >
          {detail.totals.by_kind.map((k) => (
            <DescriptionListGroup key={k.kind}>
              <DescriptionListTerm>
                {labelOf(SETTLEMENT_LINE_KIND_LABELS, k.kind)} ({k.count})
              </DescriptionListTerm>
              <DescriptionListDescription>
                <Money value={k.amount} />
              </DescriptionListDescription>
            </DescriptionListGroup>
          ))}
          <DescriptionListGroup>
            <DescriptionListTerm>Total des opérations</DescriptionListTerm>
            <DescriptionListDescription>
              <strong>
                <Money value={detail.totals.lines} />
              </strong>
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
        {rows.length === 0 ? (
          <EmptyListState title="Aucune opération" body="Ce versement ne porte que des frais ou des compensations." />
        ) : (
          <>
            <ListToolbar
              list={list}
              placeholder="Rechercher par client, facture, description, identifiant Mollie ou domaine..."
              filters={
                <>
                  <ToolbarItem>
                    <StatusMultiSelect
                      options={kindOptions}
                      selected={kinds}
                      onChange={setKinds}
                      ariaLabel="Filtrer par type d'opération"
                      label="Type"
                    />
                  </ToolbarItem>
                  <ToolbarItem alignSelf="center">
                    <Switch
                      id="settlement-flagged-only"
                      label="Signalées seulement"
                      isChecked={flaggedOnly}
                      onChange={(_e, checked) => setFlaggedOnly(checked)}
                    />
                  </ToolbarItem>
                </>
              }
              actions={
                <CsvButton
                  label="Exporter les opérations (CSV)"
                  filename={reconciliationCsvFilename.settlement(settlement.reference, settlement.id)}
                  build={() => buildSettlementLinesCsv(settlement.reference, rows)}
                />
              }
            />
            {list.filtered.length === 0 ? (
              <NoMatchState onClear={clearFilters} />
            ) : (
              <>
                <Table aria-label="Opérations portées par le versement" variant="compact" isExpandable>
                  <Thead>
                    <Tr>
                      <Th screenReaderText="Détails" />
                      <Th>Type</Th>
                      <Th>Payé le</Th>
                      <Th>Objet</Th>
                      <Th>Client</Th>
                      <Th>Description</Th>
                      <Th>Facture</Th>
                      <Th className={AMOUNT_CELL}>Montant</Th>
                      <Th>Signalements</Th>
                    </Tr>
                  </Thead>
                  {list.paginated.map((l, rowIndex) => {
                    const key = rowKey(l, rowIndex);
                    const isExpanded = expanded.has(key);
                    return (
                      <Tbody key={key} isExpanded={isExpanded}>
                        <Tr isContentExpanded={isExpanded}>
                          <Td
                            expand={{ rowIndex, isExpanded, onToggle: () => toggle(key), expandId: 'settlement-line' }}
                          />
                          <Td dataLabel="Type">{labelOf(SETTLEMENT_LINE_KIND_LABELS, l.kind)}</Td>
                          <Td dataLabel="Payé le" modifier="nowrap">
                            {formatInstantBrussels(l.paid_at ?? l.created_at)}
                          </Td>
                          <Td dataLabel="Objet">{l.purpose === null ? '—' : labelOf(PURPOSE_LABELS, l.purpose)}</Td>
                          <Td dataLabel="Client">{l.client_name ?? '—'}</Td>
                          <Td dataLabel="Description" modifier="breakWord">
                            {l.description ?? '—'}
                          </Td>
                          <Td dataLabel="Facture">{l.invoice_numero ?? '—'}</Td>
                          <Td dataLabel="Montant" className={AMOUNT_CELL}>
                            <Money value={l.amount} />
                          </Td>
                          <Td dataLabel="Signalements">
                            <FlagLabels flags={l.flags} />
                          </Td>
                        </Tr>
                        <Tr isExpanded={isExpanded}>
                          <Td />
                          <Td colSpan={LINE_COLUMN_COUNT - 1}>
                            <ExpandableRowContent>
                              <DetailList
                                items={[
                                  {
                                    term: 'Moyen de paiement',
                                    value: l.method === null ? null : labelOf(PAYMENT_METHOD_LABELS, l.method),
                                  },
                                  { term: 'Statut Mollie', value: l.status },
                                  {
                                    term: 'Créé le',
                                    value: l.created_at === null ? null : formatInstantBrussels(l.created_at),
                                  },
                                  { term: 'Identifiant', value: l.id },
                                  { term: 'Paiement Mollie', value: l.payment_id },
                                  ...attributionItems(l),
                                ]}
                              />
                            </ExpandableRowContent>
                          </Td>
                        </Tr>
                      </Tbody>
                    );
                  })}
                </Table>
                <ListPagination list={list} variant="bottom" />
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

const SettlementBody: React.FunctionComponent<{ detail: MollieSettlementDetail }> = ({ detail }) => {
  const { settlement, checks } = detail;
  return (
    <Grid hasGutter>
      <GridItem md={6}>
        <Card isFullHeight>
          <CardTitle>Versement</CardTitle>
          <CardBody>
            <DescriptionList isCompact isHorizontal>
              <DescriptionListGroup>
                <DescriptionListTerm>Référence bancaire</DescriptionListTerm>
                <DescriptionListDescription>{settlement.reference ?? '—'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Statut</DescriptionListTerm>
                <DescriptionListDescription>
                  <SettlementStatusLabel status={settlement.status} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Créé le</DescriptionListTerm>
                <DescriptionListDescription>{formatInstantBrussels(settlement.created_at)}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Versé le</DescriptionListTerm>
                <DescriptionListDescription>{formatInstantBrussels(settlement.settled_at)}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Identifiant Mollie</DescriptionListTerm>
                <DescriptionListDescription>{settlement.id}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem md={6}>
        <Card isFullHeight>
          <CardTitle>Montants et contrôles</CardTitle>
          <CardBody>
            <DescriptionList isCompact isHorizontal>
              <DescriptionListGroup>
                <DescriptionListTerm>Revenus</DescriptionListTerm>
                <DescriptionListDescription>
                  <Money value={settlement.revenue} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Frais</DescriptionListTerm>
                <DescriptionListDescription>
                  <Money value={settlement.costs} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Montant versé</DescriptionListTerm>
                <DescriptionListDescription>
                  <strong>
                    <Money value={settlement.amount} />
                  </strong>
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Transactions</DescriptionListTerm>
                <DescriptionListDescription>{settlement.transactions_count}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Montant = revenus − frais</DescriptionListTerm>
                <DescriptionListDescription>
                  <CheckLabel ok={checks.amount_is_revenue_minus_costs} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Revenus expliqués par les opérations</DescriptionListTerm>
                <DescriptionListDescription>
                  <CheckLabel ok={checks.lines_match_revenue} />
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem span={12}>
        <Card>
          <CardTitle>Périodes</CardTitle>
          <CardBody>
            {settlement.periods.length === 0 ? (
              <EmptyListState title="Aucune période" />
            ) : (
              <SettlementPeriodsTable periods={settlement.periods} ariaLabel="Périodes du versement" />
            )}
          </CardBody>
        </Card>
      </GridItem>
      {settlement.periods.map((p) => (
        <GridItem key={p.period} span={12}>
          <PeriodLinesCard period={p} />
        </GridItem>
      ))}
      <GridItem span={12}>
        <SettlementLinesSection detail={detail} />
      </GridItem>
    </Grid>
  );
};

/** The Brussels year a payout belongs to (its payout date, else its creation date) — for the way back. */
function settlementYear(detail: MollieSettlementDetail | null): string | null {
  const instant = detail?.settlement.settled_at ?? detail?.settlement.created_at ?? null;
  const ts = instant === null ? NaN : Date.parse(instant);
  return Number.isNaN(ts) ? null : brusselsToday(ts).slice(0, 4);
}

/**
 * Admin › Comptabilité › Versements › {référence} — one Mollie payout broken down
 * (`GET /admin/reconciliation/mollie/settlements/{stl_id}`). Staff-only: <NotFound /> otherwise.
 */
export function SettlementDetail() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const { settlementId } = useParams<{ settlementId: string }>();
  const id = settlementId ?? null;
  const { data, loading, error, notFound, fetchedAt, refetch } = useMollieSettlement(id, isStaff);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  const year = settlementYear(data);
  const settlementsPath = `/admin/reconciliation?tab=settlements${year ? `&year=${year}` : ''}`;
  const title = data ? `Versement ${data.settlement.reference ?? data.settlement.id}` : 'Versement';
  const showRefresh = data !== null && !notFound;

  const renderBody = () => {
    if (notFound) {
      return (
        <EmptyState titleText="Versement introuvable" icon={SearchIcon} headingLevel="h2" variant="lg">
          <EmptyStateBody>
            Mollie ne connaît pas ce versement. Revenez à la <Link to={settlementsPath}>liste des versements</Link>.
          </EmptyStateBody>
        </EmptyState>
      );
    }
    if (data === null) {
      return error !== null && !loading ? (
        <ReadErrorState title="Impossible de charger le versement" message={error} onRetry={() => void refetch()} />
      ) : (
        <LiveReadSpinner what="versement" />
      );
    }
    return (
      <>
        {error !== null && <StaleDataAlert message={error} />}
        <ReconciliationBanners warnings={data.warnings} onRetry={() => void refetch()} />
        <SettlementBody detail={data} />
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb>
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem
            render={({ className }) => (
              <Link className={className} to="/admin/reconciliation">
                Comptabilité
              </Link>
            )}
          />
          <BreadcrumbItem
            render={({ className }) => (
              <Link className={className} to={settlementsPath}>
                Versements
              </Link>
            )}
          />
          <BreadcrumbItem isActive>{data?.settlement.reference ?? id}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <MoneyBillWaveIcon />
              </Icon>
              {title}
            </Content>
            <Content component="p">
              Un versement Mollie vers le compte bancaire : ses revenus et frais par mois, et chaque opération
              qu&apos;il porte.
            </Content>
          </FlexItem>
          {showRefresh && (
            <FlexItem>
              <Flex
                direction={{ default: 'column' }}
                alignItems={{ default: 'alignItemsFlexEnd' }}
                spaceItems={{ default: 'spaceItemsXs' }}
              >
                <FlexItem>
                  <RefreshButton loading={loading} onRefresh={() => void refetch()} />
                </FlexItem>
                <FlexItem>
                  <FetchedAt fetchedAt={fetchedAt} />
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderBody()}</PageSection>
    </>
  );
}

export default SettlementDetail;
